/**
 * Empty State Component for Networks Page
 * Displays when no networks are connected
 */

import React from 'react';
import { Plus, Network } from 'lucide-react';
import { useNetworksStore } from '../../stores/networksStore';

interface EmptyStateProps {
  hasFilters?: boolean;
}

export const EmptyState: React.FC<EmptyStateProps> = ({ hasFilters = false }) => {
  const { openWizard, resetFilters } = useNetworksStore();

  if (hasFilters) {
    return (
      <div className="flex flex-col items-center justify-center py-16 px-4">
        <div className="w-16 h-16 rounded-full bg-gray-100 dark:bg-gray-800 flex items-center justify-center mb-4">
          <Network className="w-8 h-8 text-gray-400" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
          No networks match your filters
        </h3>
        <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-6">
          Try adjusting your search or filter criteria to find what you're looking for.
        </p>
        <button
          onClick={resetFilters}
          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        >
          Clear Filters
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center py-16 px-4">
      <div className="w-20 h-20 rounded-full bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center mb-6">
        <Network className="w-10 h-10 text-blue-600 dark:text-blue-400" />
      </div>
      <h3 className="text-xl font-semibold text-gray-900 dark:text-gray-100 mb-2">
        No Networks Connected
      </h3>
      <p className="text-gray-600 dark:text-gray-400 text-center max-w-md mb-8">
        Connect your first affiliate network to start tracking your performance and earnings across multiple platforms.
      </p>
      <button
        onClick={openWizard}
        className="inline-flex items-center gap-2 px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-all duration-200 shadow-sm hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
        aria-label="Add your first network"
      >
        <Plus className="w-5 h-5" />
        Add Your First Network
      </button>
    </div>
  );
};
